/**
 * React Native Telemetry & Performance Adapter
 * Provides interfaces and hooks for native thread, memory, startup, and crash monitoring.
 */

import { telemetryService } from './telemetryService';
import type {
  NativeStartupMetric,
  NativeThreadMetric,
  NativeMemoryMetric,
  NativeCrashMetric,
  NativeNetworkFailureMetric,
} from './telemetryTypes';

export interface INativeTelemetryAdapter {
  measureStartup(): Promise<NativeStartupMetric | null>;
  startThreadMonitoring(callback: (metric: NativeThreadMetric) => void): () => void;
  getMemoryInfo(): Promise<NativeMemoryMetric | null>;
  captureNativeCrash(error: NativeCrashMetric): void;
}

/**
 * Universal Native Telemetry Facade
 * Works in React Native environments while remaining fully safe in Web environments.
 */
class NativeTelemetry {
  private adapter: INativeTelemetryAdapter | null = null;

  public registerAdapter(adapter: INativeTelemetryAdapter): void {
    this.adapter = adapter;
  }

  /**
   * Monitor React Native App Startup Time
   */
  public async trackStartup(metrics: Partial<NativeStartupMetric>): Promise<void> {
    if (this.adapter) {
      const nativeMetrics = await this.adapter.measureStartup();
      if (nativeMetrics) {
        telemetryService.recordNativeStartup(nativeMetrics);
        return;
      }
    }

    // Web Fallback (uses performance.timing or performance.now)
    const paintEntries = typeof performance !== 'undefined' ? performance.getEntriesByType('paint') : [];
    const fcp = paintEntries.find((e) => e.name === 'first-contentful-paint')?.startTime;

    telemetryService.recordNativeStartup({
      coldStartTimeMs: metrics.coldStartTimeMs ?? (typeof performance !== 'undefined' ? performance.now() : 0),
      warmStartTimeMs: metrics.warmStartTimeMs,
      timeToFirstMeaningfulPaintMs: metrics.timeToFirstMeaningfulPaintMs ?? fcp,
    });
  }

  /**
   * Monitor Thread Performance (JS thread FPS & dropped frames)
   */
  public startThreadWatcher(): () => void {
    if (this.adapter) {
      return this.adapter.startThreadMonitoring((metric) => {
        telemetryService.recordNativeThread(metric);
      });
    }

    // Web Fallback: requestAnimationFrame-based JS thread frame duration estimator
    if (typeof window === 'undefined' || typeof requestAnimationFrame === 'undefined') {
      return () => {};
    }

    let isWatching = true;
    let lastFrameTime = performance.now();
    let frameCount = 0;
    let droppedFrames = 0;
    let intervalStart = performance.now();

    const loop = (now: number) => {
      if (!isWatching) return;

      const delta = now - lastFrameTime;
      lastFrameTime = now;
      frameCount++;

      // Expected frame time at 60fps is ~16.6ms. Delta > 33ms means dropped frame(s).
      if (delta > 33.3) {
        droppedFrames += Math.floor(delta / 16.6) - 1;
      }

      // Every 5 seconds, sample and report thread health
      if (now - intervalStart >= 5000) {
        const elapsedSec = (now - intervalStart) / 1000;
        const fps = Math.round(frameCount / elapsedSec);
        const jankDetected = droppedFrames > 10 || fps < 45;

        telemetryService.recordNativeThread({
          thread: 'JS',
          fps: Math.min(fps, 60),
          droppedFrames,
          frameDurationMs: delta,
          jankDetected,
        });

        // Reset window
        frameCount = 0;
        droppedFrames = 0;
        intervalStart = now;
      }

      requestAnimationFrame(loop);
    };

    const handle = requestAnimationFrame(loop);
    return () => {
      isWatching = false;
      cancelAnimationFrame(handle);
    };
  }

  /**
   * Monitor Memory Footprint
   */
  public async trackMemory(): Promise<void> {
    if (this.adapter) {
      const mem = await this.adapter.getMemoryInfo();
      if (mem) {
        telemetryService.recordNativeMemory(mem);
        return;
      }
    }

    // Web Fallback: performance.memory (Chrome / Chromium)
    const perfWithMemory = performance as unknown as {
      memory?: {
        usedJSHeapSize: number;
        totalJSHeapSize: number;
        jsHeapSizeLimit: number;
      };
    };

    if (perfWithMemory?.memory) {
      telemetryService.recordNativeMemory({
        usedJsHeapBytes: perfWithMemory.memory.usedJSHeapSize,
        totalJsHeapBytes: perfWithMemory.memory.totalJSHeapSize,
        nativeHeapUsageMb: Math.round(perfWithMemory.memory.totalJSHeapSize / (1024 * 1024)),
      });
    }
  }

  /**
   * Report React Native Uncaught Crash
   */
  public reportCrash(crash: Omit<NativeCrashMetric, 'timestamp'>): void {
    telemetryService.recordNativeCrash(crash);
  }

  /**
   * Report Network Failures (Timeout, DNS, Connection drop)
   */
  public reportNetworkFailure(failure: Omit<NativeNetworkFailureMetric, 'timestamp'>): void {
    telemetryService.recordNetworkFailure(failure);
  }
}

export const nativeTelemetry = new NativeTelemetry();
